import { useEffect, useState } from 'react'
import { Form, Outlet, useFetchers, useNavigate } from 'react-router'
import { BackButton, Button, Dialog, Divider, MenuItem, Stack, TextField, Typography } from '../../components'
import { useGlobalState } from '../../hooks'
import { StyleflowRackCards } from './StyleflowRackCards'

interface StyleflowRackProps {
  garments: TrinityAPI.VisualizerRackType[]
  stylepages: TrinityAPI.VisualizerStylepageType[]
}

export function StyleflowRack({ garments, stylepages }: StyleflowRackProps) {
  const { setToastInfo } = useGlobalState()
  const [stylepageDialog, setStylepageDialog] = useState(false)
  const navigate = useNavigate()
  const fetchers = useFetchers()

  useEffect(() => {
    const fetcher = fetchers.find(fetcher => fetcher.key === StyleflowRackCards.FETCHER_KEY)
    if (fetcher?.data) {
      setToastInfo({ show: true, severity: 'success', message: fetcher?.data?.message })
    }
  }, [fetchers, setToastInfo])

  return (
    <Stack spacing={4}>
      <BackButton to='/styleflow/collections'>Continue Browsing</BackButton>
      <Stack
        direction={{ mobile: 'column', laptop: 'row' }}
        spacing={4}
        alignItems='center'
        justifyContent='space-between'
      >
        <Typography variant='h3'>Rack</Typography>
        <Stack direction='row' spacing={4}>
          <Button
            disabled={garments.length < 1}
            size='small'
            variant='outlined'
            onClick={() => setStylepageDialog(true)}
          >
            Save As Stylepage
          </Button>
          <Button disabled={garments.length < 1} size='small' onClick={() => navigate('start-order')}>
            Start New Order
          </Button>
        </Stack>
      </Stack>
      <Divider />
      <StyleflowRackCards
        showLinks
        items={garments}
        actions={[
          { type: 'edit', title: 'Edit', intent: 'edit' },
          { type: 'delete', title: 'Remove' },
        ]}
      />
      <Dialog open={stylepageDialog} onClose={() => setStylepageDialog(false)}>
        <Form method='POST'>
          <Stack p={4} spacing={6}>
            <Typography variant='h4'>Save As Stylepage</Typography>
            <TextField required label='Stylepage Title' name='name' />
            <Button type='submit' name='intent' value='save'>
              Save
            </Button>
          </Stack>
        </Form>
      </Dialog>
      <SaveStylepageDialog open={stylepageDialog} close={() => setStylepageDialog(false)} stylepages={stylepages} />
      <Outlet />
    </Stack>
  )
}

interface DialogProps {
  open: boolean
  close: () => void
  stylepages: TrinityAPI.VisualizerStylepageType[]
}

function SaveStylepageDialog({ open, close, stylepages }: DialogProps) {
  const [selectedStylepage, setSelectedStylepage] = useState('')
  const [stylepageName, setStylepageName] = useState('')
  const [isDuplicate, setIsDuplicate] = useState(false)
  const isDisabled = !(selectedStylepage || stylepageName) || isDuplicate

  useEffect(() => {
    if (selectedStylepage) {
      setStylepageName('')
    }
  }, [selectedStylepage])

  useEffect(() => {
    setIsDuplicate(stylepages.some(stylepage => stylepage?.name === stylepageName))
  }, [stylepageName, stylepages])

  return (
    <Dialog open={open} onClose={close} PaperProps={{ sx: { px: 4 } }}>
      <Form method='POST'>
        <Stack p={4} spacing={6}>
          <Typography variant='h4'>Save As Stylepage</Typography>
          <TextField
            select
            value={selectedStylepage}
            label='Select Stylepage'
            name='stylepageId'
            onChange={e => setSelectedStylepage(e.target.value)}
          >
            <MenuItem value=''>New Stylepage</MenuItem>
            {stylepages.map(stylepage => (
              <MenuItem key={stylepage?.id} value={stylepage?.id}>
                {stylepage?.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            disabled={Boolean(selectedStylepage)}
            error={isDuplicate}
            required
            label='New Stylepage Name'
            name='name'
            value={stylepageName}
            onChange={e => setStylepageName(e.target.value)}
            helperText={isDuplicate ? 'You already have a Stylepage with this name' : null}
          />
          <Button disabled={isDisabled} type='submit' name='intent' value='save'>
            Save
          </Button>
        </Stack>
      </Form>
    </Dialog>
  )
}
